import { useState } from "react";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { useLayout } from "../../hooks/useWindowWidth";
import { useThemeContext } from "../../theme/ThemeContext";
import { overlayBackdrop } from "../../theme/styles";
import { RADIUS } from "../../theme/tokens";
import { Button } from "../shared";
import { useSwipeToClose } from "../../hooks/useSwipeToClose";

const AMENITY_OPTIONS = [
  { value: "hotel",  label: "Hotel" },
  { value: "meals",  label: "Meals" },
  { value: "both",   label: "Hotel + Meals" },
];

export function AmenityRequestModal({ entry, onClose, onSend }) {
  const { SA, GLASS, tokens, TYPOGRAPHY } = useThemeContext();
  useLockBodyScroll();
  useEscapeKey(onClose);
  const [amenityType, setAmenityType] = useState("both");
  const [note, setNote]               = useState("");
  const [submitting, setSubmitting]   = useState(false);
  const { isWide } = useLayout();
  const { handleProps, dragStyle } = useSwipeToClose(onClose, { enabled: !isWide });

  const handleSend = async () => {
    setSubmitting(true);
    await onSend({ amenityType, note: note.trim() });
    setSubmitting(false);
  };

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ ...overlayBackdrop, zIndex: 200, display: "flex",
        alignItems: isWide ? "center" : "flex-end",
        justifyContent: "center", padding: isWide ? 24 : 0 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...GLASS.modal,
        width: "100%", maxWidth: isWide ? 400 : "100%",
        borderRadius: isWide ? 16 : "16px 16px 0 0",
        maxHeight: "88vh", display: "flex", flexDirection: "column", ...dragStyle }}>

        {!isWide && (
          <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px",
                        flexShrink: 0, touchAction: "none", cursor: "grab" }} {...handleProps}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: SA.borderGray }} />
          </div>
        )}
        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: `1px solid ${SA.borderGray}`,
          display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, letterSpacing: "0.18em",
              textTransform: "uppercase", marginBottom: 4 }}>
              Request Amenities
            </div>
            <div style={{ fontSize: TYPOGRAPHY.sub, color: SA.lightSilver,
              fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
              {entry.inFlt} {"->"} {entry.outFlt}
              {entry.dest ? <span style={{ color: SA.silver }}> · {entry.dest}</span> : null}
            </div>
          </div>
          <Button variant="icon" onClick={onClose}
            style={{ background: SA.medGray, color: SA.silver, width: 28, height: 28, fontSize: 16, flexShrink: 0 }}>×</Button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          {/* Delay context note */}
          <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, letterSpacing: "0.04em",
            marginBottom: 16, lineHeight: 1.5 }}>
            Inbound {entry.inFlt} is delayed ({entry.ata ? `ATA ${entry.ata}` : `ETA ${entry.eta}`}, STA {entry.sta}).
            Select amenities to request from United.
          </div>

          {/* Amenity type selector */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: "block", fontSize: TYPOGRAPHY.caption, color: SA.silver,
              letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>
              Amenity Type
            </label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {AMENITY_OPTIONS.map(opt => (
                <button key={opt.value} onClick={() => setAmenityType(opt.value)}
                  style={{
                    padding: "8px 16px", borderRadius: RADIUS.pill, cursor: "pointer",
                    fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                    fontSize: TYPOGRAPHY.caption, fontWeight: 600, letterSpacing: "0.08em",
                    border: amenityType === opt.value
                      ? `1px solid ${tokens.partner}`
                      : `1px solid ${SA.borderGray}`,
                    background: amenityType === opt.value
                      ? tokens.partnerSurface
                      : "transparent",
                    color: amenityType === opt.value ? tokens.partner : SA.silver,
                    transition: "all 0.15s",
                  }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Optional note */}
          <div>
            <label style={{ display: "block", fontSize: TYPOGRAPHY.caption, color: SA.silver,
              letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>
              Note to SCCO (optional)
            </label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder=""
              rows={3}
              style={{ width: "100%", boxSizing: "border-box",
                background: SA.darkGray, border: `1px solid ${SA.borderGray}`,
                borderRadius: RADIUS.md, color: SA.white,
                padding: "12px 14px", fontSize: TYPOGRAPHY.note,
                fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                outline: "none", resize: "none", lineHeight: 1.5 }}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 20px 16px", borderTop: `1px solid ${SA.borderGray}` }}>
          <div style={{ display: "flex", gap: 10 }}>
            <Button variant="secondary" onClick={onClose}
              style={{ flex: 1, padding: "13px", fontSize: TYPOGRAPHY.caption,
                letterSpacing: "0.12em", textTransform: "uppercase",
                fontFamily: "'Helvetica Neue',Arial,sans-serif",
                borderRadius: RADIUS.md }}>
              Cancel
            </Button>
            <Button onClick={handleSend} disabled={submitting}
              style={{ flex: 2, padding: "13px",
                background: !submitting ? tokens.partnerSurface : SA.darkGray,
                border: !submitting ? `1px solid ${tokens.partnerBorder}` : `1px solid ${SA.borderGray}`,
                color: !submitting ? tokens.partner : SA.silver,
                fontSize: TYPOGRAPHY.body, letterSpacing: "0.1em", textTransform: "uppercase",
                fontFamily: "'Helvetica Neue',Arial,sans-serif", fontWeight: 700,
                borderRadius: RADIUS.md, transition: "all 0.2s" }}>
              {submitting ? "Sending..." : "Send Request"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
